import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';
import { useQuickView } from '../../../context/QuickViewContext';
import { useCompare } from '../../../context/CompareContext';
import { useWishlist } from '../../../context/WishlistContext';

const kits = [
  {
    id: 1,
    name: 'Essential Starter Kit',
    description: 'Everything a beginner needs to start crafting cocktails at home.',
    price: 169,
    originalPrice: 219,
    rating: 4.9,
    reviews: 234,
    tools: 12,
    ingredients: 8,
    badge: 'Best Seller',
    image:
      'https://public.readdy.ai/ai/img_res/e8cba6e89706edbc15e6226922bd305b.jpg',
  },
  {
    id: 2,
    name: 'Professional Bartender Set',
    description: 'Premium tools used by professional bartenders worldwide.',
    price: 329,
    originalPrice: 429,
    rating: 5.0,
    reviews: 156,
    tools: 24,
    ingredients: 12,
    badge: 'Pro Choice',
    image:
      'https://readdy.ai/api/search-image?query=professional%20bartender%20tool%20set%20luxury%20presentation%2C%20gold%20and%20copper%20bar%20tools%2C%20premium%20cocktail%20making%20equipment%2C%20elegant%20gift%20box%20packaging%2C%20high-end%20bar%20accessories%20photography&width=400&height=400&seq=kit-glam-002&orientation=squarish',
  },
  {
    id: 3,
    name: 'Whiskey Cocktail Kit',
    description: 'Specialized kit for crafting perfect whiskey-based cocktails.',
    price: 229,
    originalPrice: 299,
    rating: 4.8,
    reviews: 189,
    tools: 10,
    ingredients: 15,
    badge: 'Specialty',
    image:
      'https://readdy.ai/api/search-image?query=whiskey%20cocktail%20making%20kit%20with%20premium%20bourbon%20bottle%2C%20ice%20sphere%20mold%2C%20mixing%20glass%2C%20bitters%20collection%2C%20elegant%20dark%20wood%20presentation%2C%20luxury%20spirits%20gift%20set%20photography&width=400&height=400&seq=kit-glam-003&orientation=squarish',
  },
  {
    id: 4,
    name: 'Tropical Tiki Collection',
    description: 'Create exotic tiki drinks with authentic tools and ingredients.',
    price: 199,
    originalPrice: 259,
    rating: 4.7,
    reviews: 142,
    tools: 8,
    ingredients: 18,
    badge: 'Fun Pick',
    image:
      'https://readdy.ai/api/search-image?query=tropical%20tiki%20cocktail%20kit%20with%20ceramic%20tiki%20mugs%2C%20bamboo%20accessories%2C%20exotic%20fruit%20syrups%2C%20colorful%20umbrella%20garnishes%2C%20fun%20summer%20bar%20set%20photography%2C%20vibrant%20tropical%20aesthetic&width=400&height=400&seq=kit-glam-004&orientation=squarish',
  },
  {
    id: 5,
    name: 'Molecular Mixology Set',
    description: 'Advanced kit for creating avant-garde cocktail experiences.',
    price: 389,
    originalPrice: 519,
    rating: 4.9,
    reviews: 87,
    tools: 20,
    ingredients: 25,
    badge: 'Advanced',
    image:
      'https://readdy.ai/api/search-image?query=molecular%20mixology%20kit%20with%20scientific%20equipment%2C%20spherification%20tools%2C%20agar%20agar%2C%20sodium%20alginate%2C%20modern%20gastronomy%20bar%20set%2C%20futuristic%20cocktail%20making%20equipment%20photography&width=400&height=400&seq=kit-glam-005&orientation=squarish',
  },
];

const bundles = [
  {
    id: 'bundle-1',
    icon: 'ri-book-open-line',
    title: 'Manual Bundle',
    desc: 'All 6 training manuals',
    price: 319,
    originalPrice: 534,
    gradient: 'from-sky-600 to-sky-500',
    btnClass: 'bg-white text-stone-900 hover:bg-sky-50',
  },
  {
    id: 'bundle-2',
    icon: 'ri-box-3-line',
    title: 'Premium Starter Kit',
    desc: 'Tools + ingredients + manual',
    price: 259,
    originalPrice: 388,
    gradient: 'from-amber-500 to-orange-400',
    btnClass: 'bg-stone-900 text-white hover:bg-stone-800',
    badge: 'BEST VALUE',
  },
  {
    id: 'bundle-3',
    icon: 'ri-gift-line',
    title: 'Ultimate Package',
    desc: 'Class + kit + all manuals',
    price: 589,
    originalPrice: 922,
    gradient: 'from-rose-500 to-pink-400',
    btnClass: 'bg-white text-stone-900 hover:bg-rose-50',
    badge: 'MOST POPULAR',
  },
];

export default function Kits() {
  const [activeKit, setActiveKit] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: carouselRef, isVisible: carouselVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  const { ref: bundlesRef, isVisible: bundlesVisible } = useScrollAnimation();
  const { addToCart } = useCart();
  const { openQuickView } = useQuickView();
  const { addToCompare, removeFromCompare, isInCompare, compareItems } = useCompare();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const toggleWishlist = (kit: typeof kits[0]) => {
    const wishlistId = `kit-${kit.id}`;
    if (isInWishlist(wishlistId)) {
      removeFromWishlist(wishlistId);
    } else {
      addToWishlist({
        id: wishlistId,
        name: kit.name,
        price: kit.price,
        originalPrice: kit.originalPrice,
        image: kit.image,
        type: 'kit',
        description: kit.description,
        badge: kit.badge,
        rating: kit.rating,
        reviews: kit.reviews,
      });
    }
  };

  const toggleCompare = (kit: typeof kits[0]) => {
    if (isInCompare(kit.id)) {
      removeFromCompare(kit.id);
    } else {
      addToCompare(kit);
    }
  };

  const scrollLeft = () => {
    setActiveKit((prev) => (prev > 0 ? prev - 1 : kits.length - 1));
  };

  const scrollRight = () => {
    setActiveKit((prev) => (prev < kits.length - 1 ? prev + 1 : 0));
  };

  const handleAddKitToCart = (kit: typeof kits[0]) => {
    addToCart({
      id: `kit-${kit.id}`,
      name: kit.name,
      price: kit.price,
      originalPrice: kit.originalPrice,
      image: kit.image,
      type: 'kit',
      description: kit.description,
    });
  };

  const handleAddBundleToCart = (bundle: typeof bundles[0]) => {
    addToCart({
      id: bundle.id,
      name: bundle.title,
      price: bundle.price,
      originalPrice: bundle.originalPrice,
      type: 'bundle',
      description: bundle.desc,
    });
  };

  const handleQuickViewKit = (kit: typeof kits[0]) => {
    openQuickView({
      id: `kit-${kit.id}`,
      name: kit.name,
      description: kit.description,
      price: kit.price,
      originalPrice: kit.originalPrice,
      image: kit.image,
      type: 'kit',
      badge: kit.badge,
      details: [
        { label: 'Tools Included', value: `${kit.tools} pieces`, icon: 'ri-tools-line' },
        { label: 'Ingredients', value: `${kit.ingredients} items`, icon: 'ri-flask-line' },
        { label: 'Rating', value: `${kit.rating} (${kit.reviews} reviews)`, icon: 'ri-star-fill' },
        { label: 'Skill Level', value: kit.badge === 'Advanced' ? 'Advanced' : 'All Levels', icon: 'ri-bar-chart-line' },
      ],
      extras: ['Free shipping', 'Recipe cards included', '30-day guarantee'],
    });
  };

  const handleQuickViewBundle = (bundle: typeof bundles[0]) => {
    openQuickView({
      id: bundle.id,
      name: bundle.title,
      description: bundle.desc,
      price: bundle.price,
      originalPrice: bundle.originalPrice,
      type: 'bundle',
      badge: bundle.badge,
      details: [
        { label: 'Savings', value: `$${bundle.originalPrice - bundle.price}`, icon: 'ri-price-tag-3-line' },
        { label: 'Items', value: 'Multiple products', icon: 'ri-stack-line' },
      ],
      extras: ['Free shipping', 'Gift wrapping available', 'Best value bundle'],
    });
  };

  return (
    <section id="kits" className="py-32 bg-stone-950 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-400/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-20 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-500"></div>
            <span className="text-amber-400 text-xs font-semibold tracking-[0.25em] uppercase">Shop Premium Equipment</span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-500"></div>
          </div>
          <h2 className="text-5xl md:text-6xl font-display font-normal text-white mb-5">
            Curated Cocktail Kits
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto leading-relaxed">
            Everything you need to craft professional cocktails at home. Premium tools and
            ingredients in beautifully packaged kits.
          </p>
        </div>

        {/* Kits Carousel */}
        <div 
          ref={carouselRef}
          className={`relative scroll-animate ${carouselVisible ? 'visible' : ''}`} 
          data-product-shop
        >
          <div className="flex gap-8 overflow-x-auto pb-8 scrollbar-hide snap-x snap-mandatory px-4">
            {kits.map((kit, index) => (
              <div
                key={kit.id}
                className={`flex-shrink-0 w-80 bg-white rounded-3xl overflow-hidden transition-all duration-500 snap-center ${
                  index === activeKit ? 'scale-105 shadow-2xl shadow-amber-500/10' : 'scale-100 shadow-xl'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Image */}
                <div className="relative h-72 bg-gradient-to-br from-amber-50 to-stone-50 p-6 group">
                  <div className="absolute top-4 left-4 px-4 py-1.5 bg-stone-900 text-white text-xs font-semibold rounded-full">
                    {kit.badge}
                  </div>
                  {/* Top Right Buttons */}
                  <div className="absolute top-4 right-4 flex flex-col gap-2">
                    <button
                      onClick={() => toggleWishlist(kit)}
                      className="w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                    >
                      <i
                        className={`text-lg ${
                          isInWishlist(`kit-${kit.id}`)
                            ? 'ri-heart-fill text-rose-500'
                            : 'ri-heart-line text-stone-300'
                        }`}
                      ></i>
                    </button>
                    <button
                      onClick={() => toggleCompare(kit)}
                      disabled={!isInCompare(kit.id) && compareItems.length >= 3}
                      className={`w-11 h-11 rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-all ${
                        isInCompare(kit.id)
                          ? 'bg-amber-400 text-stone-900'
                          : compareItems.length >= 3
                          ? 'bg-white/50 text-stone-300 cursor-not-allowed'
                          : 'bg-white text-stone-400 hover:text-amber-500'
                      }`}
                      title={isInCompare(kit.id) ? 'Remove from compare' : compareItems.length >= 3 ? 'Max 3 items' : 'Add to compare'}
                    >
                      <i className="ri-scales-3-line text-lg"></i>
                    </button>
                  </div>
                  <img
                    src={kit.image}
                    alt={kit.name}
                    className="w-full h-full object-contain hover:scale-110 transition-transform duration-500"
                  />
                  {/* Quick View Button */}
                  <button
                    onClick={() => handleQuickViewKit(kit)}
                    className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-lg flex items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 cursor-pointer hover:bg-stone-900 hover:text-white"
                  >
                    <i className="ri-eye-line text-sm"></i>
                    <span className="text-xs font-semibold whitespace-nowrap">Quick View</span>
                  </button>
                </div>

                {/* Content */}
                <div className="p-7">
                  <h3 className="text-xl font-serif font-semibold text-stone-900 mb-2">{kit.name}</h3>
                  <p className="text-stone-500 text-sm mb-5 leading-relaxed">{kit.description}</p>

                  <div className="flex items-center gap-5 text-sm text-stone-400 mb-5">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-tools-line text-amber-500"></i>
                      {kit.tools} tools
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-flask-line text-amber-500"></i>
                      {kit.ingredients} items
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-5">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-sm ${
                            i < Math.floor(kit.rating) ? 'text-amber-400' : 'text-stone-200'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-sm text-stone-600 font-medium">{kit.rating}</span>
                    <span className="text-sm text-stone-400">({kit.reviews})</span>
                  </div>

                  <div className="flex items-center justify-between pt-5 border-t border-stone-100">
                    <div>
                      <span className="text-3xl font-display text-stone-900">
                        ${kit.price}
                      </span>
                      <span className="text-stone-300 line-through text-sm ml-2">
                        ${kit.originalPrice}
                      </span>
                    </div>
                    <button 
                      onClick={() => handleAddKitToCart(kit)}
                      className="w-12 h-12 rounded-full bg-stone-900 text-white flex items-center justify-center hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 hover:text-stone-900 transition-all cursor-pointer"
                    >
                      <i className="ri-shopping-cart-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 w-14 h-14 rounded-full bg-white/95 backdrop-blur-sm shadow-2xl flex items-center justify-center cursor-pointer hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 transition-all z-10"
          >
            <i className="ri-arrow-left-line text-xl text-stone-700"></i>
          </button>
          <button
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 w-14 h-14 rounded-full bg-white/95 backdrop-blur-sm shadow-2xl flex items-center justify-center cursor-pointer hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 transition-all z-10"
          >
            <i className="ri-arrow-right-line text-xl text-stone-700"></i>
          </button>
        </div>

        {/* Bundle Cards */}
        <div 
          ref={bundlesRef}
          className={`grid md:grid-cols-3 gap-6 mt-20 stagger-children ${bundlesVisible ? 'visible' : ''}`}
        >
          {bundles.map((bundle, i) => (
            <div
              key={i}
              className={`relative bg-gradient-to-br ${bundle.gradient} rounded-3xl p-8 text-center overflow-hidden group`}
            >
              {bundle.badge && (
                <div className="absolute top-4 right-4 px-3 py-1 bg-white text-stone-900 text-[10px] font-bold rounded-full tracking-wider">
                  {bundle.badge}
                </div>
              )}
              {/* Quick View for Bundle */}
              <button
                onClick={() => handleQuickViewBundle(bundle)}
                className="absolute top-4 left-4 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer hover:bg-white/40"
              >
                <i className="ri-eye-line text-white text-sm"></i>
              </button>
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-5 group-hover:scale-110 transition-transform">
                <i className={`${bundle.icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-xl font-serif font-semibold text-white mb-2">{bundle.title}</h3>
              <p className="text-white/70 text-sm mb-5">{bundle.desc}</p>
              <p className="text-3xl font-display text-white mb-1">
                ${bundle.price}
              </p>
              <p className="text-white/50 line-through text-sm mb-6">${bundle.originalPrice}</p>
              <button 
                onClick={() => handleAddBundleToCart(bundle)}
                className={`w-full py-3.5 ${bundle.btnClass} font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap`}
              >
                Add to Cart
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
